/**
 * 文件夹同步切片 · refreshTick 触发左侧 FolderList 重拉 + recentlyCreatedIds 进场动效
 *
 * 用法：
 *   建夹完成后 → dispatch(bumpFolderRefreshTick({ newIds: [...] }))
 *   FolderList 用 selectFolderRefreshTick 作为 useEffect 依赖，变动时重新拉接口
 *   FolderCard 用 selectRecentlyCreatedFolderIds 给新建的 folder 加进场动画
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface FolderSyncState {
  /** 单调递增计数器 · 每次 bump +1 */
  refreshTick: number
  /**
   * 最近一次"AI 建夹"动作创建的 folderID 列表（字符串化）
   *
   * FolderCard 渲染时若卡片 id 在此集合，加 .new class 触发淡入 + 微缩放动画。
   * 集合在下一次 bump 时被覆盖，保证动画只对最新创建的一组生效。
   * 生命周期：~2s（AIClassifyPage useEffect 用 setTimeout 后 dispatch clearRecentlyCreated）
   */
  recentlyCreatedIds: string[]
  /**
   * 新建但用户尚未进入查看的 folderID 列表 · 用于 NEW 角标
   *
   * 与 recentlyCreatedIds 区别：
   *  · recentlyCreatedIds 是动效用 · 短生命周期（~2s）
   *  · newBadgeFolderIds 是"未读"标记 · 用户没点过这张卡之前一直保留
   *  · 用户点击 folder 进入详情 → markFolderEntered 移除该 id
   *  · 进程内 state · 跨刷新清空（产品体感：刷新即视为"看过了"足够）
   *
   * 入队时机（v2 调整）：
   *  · 旧版本是 bumpFolderRefreshTick 创建文件夹时立刻入队 → "添加中"和"NEW"会同时显示
   *  · 新版本只在 taskCompleted 时入队（addCase 监听 taskSlice.taskCompleted）
   *    确保"添加中"和"NEW"互斥 · NEW 仅在归档完成后才挂在右上角
   *  · 手动建夹（不走归档任务）的 folder 也由 bumpFolderRefreshTick 入队（保留兼容）
   *    手动建夹直接进入空文件夹查看，不存在"添加中"状态，互斥语义自动满足
   */
  newBadgeFolderIds: string[]
}

const initialState: FolderSyncState = {
  refreshTick: 0,
  recentlyCreatedIds: [],
  newBadgeFolderIds: []
}

const folderSyncSlice = createSlice({
  name: 'aiClassify/folderSync',
  initialState,
  reducers: {
    bumpFolderRefreshTick: {
      reducer(
        state,
        action: PayloadAction<{
          newIds?: Array<string | number>
          /**
           * true = 不重置 recentlyCreatedIds（用于轮询场景的"刷新 photoCount
           * 但不影响进场动画"）。默认 false：用户主动建夹后调，会重写为新批次。
           */
          preserveRecentlyCreated?: boolean
          /**
           * true = 同时把 newIds 加入 newBadgeFolderIds · 用于手动建夹（无归档任务）
           * 默认 false · AI 建夹会触发归档任务，NEW 在 taskCompleted 时再入队
           */
          markAsNewBadge?: boolean
        }>
      ) {
        state.refreshTick += 1
        if (!action.payload.preserveRecentlyCreated) {
          const ids = (action.payload.newIds ?? []).map(String)
          state.recentlyCreatedIds = ids
          // 仅手动建夹场景才在这里入队 NEW · AI 建夹的 NEW 由 taskCompleted addCase 入
          if (action.payload.markAsNewBadge) {
            for (const id of ids) {
              if (!state.newBadgeFolderIds.includes(id)) {
                state.newBadgeFolderIds.push(id)
              }
            }
          }
        }
      },
      prepare(payload?: {
        newIds?: Array<string | number>
        preserveRecentlyCreated?: boolean
        markAsNewBadge?: boolean
      }) {
        return {
          payload: {
            newIds: payload?.newIds ?? [],
            preserveRecentlyCreated: !!payload?.preserveRecentlyCreated,
            markAsNewBadge: !!payload?.markAsNewBadge
          }
        }
      }
    },
    /** 进场动画播完后清空 · 避免重复刷新时残留 .new class */
    clearRecentlyCreated(state) {
      state.recentlyCreatedIds = []
    },
    /** 用户点击 folder 进入详情 · 把该 id 从 NEW 角标列表移除 */
    markFolderEntered(state, action: PayloadAction<{ folderId: string }>) {
      state.newBadgeFolderIds = state.newBadgeFolderIds.filter(
        (id) => id !== action.payload.folderId
      )
    },
    /**
     * 归档任务完成 · 把 folder 加入 NEW 角标列表
     *  · 由 useArchiveTaskPolling 在 task 转 completed 时调用
     *  · folderID 直接来自 task.folderID（poll 拿得到）· 比 listening
     *    taskCompleted action 在本 slice 中无法访问 task 列表的问题更清晰
     *  · 与"添加中"徽章互斥：添加中显示在 task.status=='archiving'，NEW 显示在
     *    folderSync.newBadgeFolderIds 包含此 id（即 taskCompleted 触发后）
     */
    markFolderArchivedComplete(
      state,
      action: PayloadAction<{ folderId: string }>
    ) {
      const id = action.payload.folderId
      if (!state.newBadgeFolderIds.includes(id)) {
        state.newBadgeFolderIds.push(id)
      }
    }
  }
})

export const {
  bumpFolderRefreshTick,
  clearRecentlyCreated,
  markFolderEntered,
  markFolderArchivedComplete
} = folderSyncSlice.actions

export default folderSyncSlice.reducer
